import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription, fromEvent, timer } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { SubSink } from 'subsink';
import { TimeoutWarningComponent } from '../../component/timeout-warning/timeout-warning.component';
import { AuthService } from 'src/app/auth/service/auth/auth.service';
import { CrossStorageService } from '../cross-storage/cross-storage.service';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private subs = new SubSink();

  public static IDLE_SESSION_TIME = "IDLE_TIME"
  public static POPUP_ACTIVE = "POPUP_ACTIVE"

  timeRemaining: BehaviorSubject<number> = new BehaviorSubject(0);

  idleSessionSubscription: Subscription;

  dialogRef: MatDialogRef<TimeoutWarningComponent>;

  private events = [
    'keydown',
    'mousemove',
    'onscroll',
    'mousewheel',
    'mousedown',
    'touchstart',
    'touchmove',
    'scroll',
  ]

  constructor(
    private authService: AuthService,
    private dialog: MatDialog,
    private appService: AppService,
    private crossStorageService: CrossStorageService,
  ) {
    this.initializeIdleSessionTime();
    this.initializePopupActive();
    this.setInterruptors();
    this.startTimer();
  }

  initializeIdleSessionTime() {
    this.resetIdleSessionTime();
  }

  initializePopupActive() {
    this.setPopupActive(
      this.getPopupActive() ?? false
    );
  }

  getIdleSessionTime(): number {
    return parseInt(
      localStorage.getItem(SessionService.IDLE_SESSION_TIME) ?? "0"
    )
  }

  resetIdleSessionTime() {
    localStorage.setItem(SessionService.IDLE_SESSION_TIME, this.getCurrentTimeInSeconds().toString());
    this.crossStorageService.resetIdleTimeInAuth(localStorage.getItem(SessionService.IDLE_SESSION_TIME));
  }

  removeIdleSessionTime() {
    localStorage.removeItem(SessionService.IDLE_SESSION_TIME);
  }

  getPopupActive(): boolean {
    return JSON.parse(
      localStorage.getItem(SessionService.POPUP_ACTIVE) ?? "false"
    )
  }

  setPopupActive(active: boolean) {
    localStorage.setItem(SessionService.POPUP_ACTIVE, active.toString());
  }

  removePopupActive() {
    localStorage.removeItem(SessionService.POPUP_ACTIVE);
  }

  getCurrentTimeInSeconds(): number {
    return Math.floor(new Date().getTime() / 1000);
  }

  setInterruptors() {
    this.events.forEach(e => {
      this.subs.sink = fromEvent(window, e).subscribe((e) => this.onInteraction());
    })
  }

  subscribeToUserLogin() {
    this.subs.sink = this.authService.getUserLoggedIn().subscribe({
      next: (loggedIn) => {
        if (loggedIn) {
          this.initializeIdleSessionTime();
          this.initializePopupActive();
        } else {
          this.removeSessionInformation();
        }
      }
    })
  }

  startTimer() {
    this.subs.sink = timer(0, 1000).subscribe(() => {
      if (this.authService.isLoggedIn()) {
        const newSessionTime: number = this.getCurrentTimeInSeconds() - this.getIdleSessionTime();
        const extendedPeriod = this.appService.getExtendedTime();
        const timeoutPeriod = parseInt(this.authService.getRefreshTokenTime()) - extendedPeriod;
        const timeRemaining = (timeoutPeriod + extendedPeriod) - newSessionTime;
        this.timeRemaining.next(timeRemaining); // Only used for showing timeout popup
        const isSessionExpired = timeRemaining < 0;
        if (isSessionExpired) {
          this.logout();
          return;
        }
        const showSessionPopup = newSessionTime > timeoutPeriod;
        if (document.visibilityState === 'visible' && showSessionPopup && !this.getPopupActive()) {
          this.dialog.closeAll();
          this.dialogRef = this.dialog.open(TimeoutWarningComponent, {
            data: {
              title: 'Session Expire Warning',
              message: `Your session is about to expire in `,
              showTimeRemaining: true,
              btnOk: 'Continue',
              btnCancel: 'Logout'
            },
            disableClose: true,
          });
          this.setPopupActive(true);
        }
      }
    });
  }

  onInteraction() {
    if (!this.getPopupActive())
      this.resetIdleSessionTime();
  }

  logout() {
    this.endSession();
    this.crossStorageService.logout();
  }

  endSession() {
    this.dialog.closeAll();
    this.dialogRef = this.dialog.open(TimeoutWarningComponent, {
      data: {
        title: 'Session Expired',
        message: `Your session has expired`,
        btnOk: 'Ok',
      },
      panelClass: 'warning-dialog'
    },);
    this.removeSessionInformation();
  }

  removeSessionInformation() {
    this.removeIdleSessionTime();
    this.removePopupActive();
  }

  ngOnDestroy(): void {
    this.removeSessionInformation();
    this.subs.unsubscribe();
  }
}
